<template>
  <nav
    class="z-10 mx-auto flex w-fit max-w-[calc(100%-16px)] justify-center bg-transparent lg:hidden"
  >
    <div
      class="bottom-nav-shell items-center gap-[2px] rounded-full px-[10px] py-[5px]"
      :style="{ '--nav-count': announcementIconSwap.length }"
    >
      <BottomNavButton
        v-for="(button, i) in announcementIconSwap"
        :key="i"
        :label="button.label"
        :to="button.to"
        :is-external="button.isExternal"
        :is-disabled="button.isDisabled"
        :active-path="button.activePath"
        :icon="button.icon"
        :icon-size="button.iconSize"
        :data-event-tracking="button.dataEventTracking"
        :data-test-id="button.dataTestId"
        @clicked-nav-button="onNavClick()"
      />
    </div>
  </nav>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import BottomNavButton from './BottomNavButton.vue';
import platformMixin from '@/mixins/platformMixin';
import HomeIcon from '@/components/icons/Home';
import HomeGICIcon from '@/components/icons/HomeGIC';
import AnnouncementIcon from '@/components/icons/AnnouncementIcon';
import AnnouncementUnreadIcon from '@/components/icons/AnnouncementUnreadIcon';
import ScheduleIcon from '@/components/icons/Schedule';
import ScheduleGICIcon from '@/components/icons/ScheduleGIC';
import ResourcesIcon from '@/components/icons/Resources';
import ResourcesGICIcon from '@/components/icons/ResourcesGIC';
import TeamIcon from '@/components/icons/Team';
import { getConferenceType, getCurrentConferenceType } from '@/utils/conference-type';
import { getCurrentLocale } from '@/utils/deviceLocale';
import { buildPhotodropMementoUrl, hasMementoAccess } from '@/utils/memento';

export default {
  mixins: [platformMixin],
  components: { BottomNavButton },
  computed: {
    ...mapGetters('announcements', ['getUnreadCount']),
    ...mapGetters('profile', ['isAbsentAlumni']),
    ...mapState('profile', [
      'confYear',
      'conferences',
      'hasOldPhotodrop',
      'name',
      'email',
      'country',
      'groupName',
    ]),

    // Alumni delegates have no current conference (so no schedule): keep PhotoDrop
    // in the middle slot for them, as before. Everyone else gets Schedule.
    //
    // PhotoDrop destination for absent alumni depends on two independent facts:
    // - hasMementoAccess: attended GPC 2026 (has something new to view)
    // - hasOldPhotodrop: linked to an OLD_CONF_IDS conference (has history to browse)
    // Both → the welcome page (offers both options). Only 2026 access, no old
    // history → skip the welcome page entirely and go straight to the external
    // Memento link, since "Browse past years" would have nothing to show anyway.
    // Neither, or only old history → the old gallery.
    scheduleOrPhotodropButton() {
      if (this.isAbsentAlumni) {
        const base = {
          icon: '/assets/icons/navbar-photdrop-hollow.svg',
          iconSize: '26px',
          label: 'PhotoDrop',
          dataEventTracking: 'gensapp-menu-photodrop',
          dataTestId: 'gensapp-menu-photodrop',
        };
        const hasAccess = hasMementoAccess(this.conferences);

        if (hasAccess && this.hasOldPhotodrop) {
          return { ...base, to: '/photodrop/welcome', activePath: '/photodrop/welcome' };
        }
        if (hasAccess) {
          return {
            ...base,
            isExternal: true,
            to: buildPhotodropMementoUrl({
              name: this.name,
              email: this.email,
              country: this.country,
              groupName: this.groupName,
            }),
          };
        }
        return { ...base, to: '/photodrop', activePath: '/photodrop' };
      }
      return {
        to: '/schedule',
        activePath: '/schedule',
        icon: '/assets/icons/navbar-schedule-hollow.svg',
        iconSize: '26px',
        label: this.$t('copy.bottomNav.schedule'),
        dataEventTracking: 'gensapp-menu-schedule',
        dataTestId: 'gensapp-menu-schedule',
      };
    },

    filteredButtons() {
      return this.buttons.filter();
    },
    navButtonWidth() {
      return '';
    },
    announcementIconSwap() {
      this.$route.path; // reactive dependency — re-evaluate when route changes so localStorage is re-read
      let buttons = [];
      const currentConfType = getCurrentConferenceType(this.confYear, this.conferences);

      if (currentConfType === 'GIC') {
        buttons = this.gicButtons;
      } else if (currentConfType === 'Regional') {
        buttons = this.gpcrButtons;
      } else {
        buttons = this.gpcButtons;
      }

      const icon =
        this.getUnreadCount(getConferenceType()) > 0 ? AnnouncementUnreadIcon : AnnouncementIcon;
      return buttons.map(btn => (btn.to.includes('announcement') ? { ...btn, icon } : btn));
    },
    gpcButtons() {
      return [
        {
          to: '/home',
          icon: '/assets/icons/navbar-home-hollow.svg',
          label: this.$t('copy.bottomNav.home'),
          dataEventTracking: 'gensapp-menu-home',
          dataTestId: 'gensapp-menu-home',
        },
        {
          to: '/resources/sessions',
          icon: '/assets/icons/navbar-resources-hollow.svg',
          iconSize: '26px',
          label: this.$t('copy.bottomNav.resources'),
          dataEventTracking: 'gensapp-menu-resources',
          dataTestId: 'gensapp-menu-resources',
        },
        this.scheduleOrPhotodropButton,
        {
          to: '/profile',
          icon: '/assets/icons/navbar-profile-hollow.svg',
          label: 'Profile',
          dataEventTracking: 'gensapp-menu-profile',
          dataTestId: 'gensapp-menu-profile',
        },
        {
          to: '/more',
          icon: '/assets/icons/navbar-more-hollow.svg',
          label: 'More',
          dataEventTracking: 'gensapp-menu-more',
          dataTestId: 'gensapp-menu-more',
        },
      ];
    },
    gpcrButtons() {
      return [
        {
          to: '/gpcr/home',
          icon: HomeIcon,
          label: this.$t('copy.bottomNav.home'),
          dataEventTracking: 'gensapp-menu-home',
          dataTestId: 'gensapp-menu-home',
        },
        {
          to: '/gpcr/resources/sessions',
          icon: ResourcesIcon,
          label: this.$t('copy.bottomNav.resources'),
          dataEventTracking: 'gensapp-menu-resources',
          dataTestId: 'gensapp-menu-resources',
        },
        {
          to: '/gpcr/schedule',
          icon: ScheduleIcon,
          label: this.$t('copy.bottomNav.schedule'),
          dataEventTracking: 'gensapp-menu-schedule',
          dataTestId: 'gensapp-menu-schedule',
        },
        {
          to: '/gpcr/team',
          icon: TeamIcon,
          label: this.$t('copy.bottomNav.team'),
          dataEventTracking: 'gensapp-menu-team',
          dataTestId: 'gensapp-menu-team',
        },
        {
          to: '/gpcr/announcement',
          icon: AnnouncementIcon,
          label: this.$t('copy.bottomNav.updates'),
          dataEventTracking: 'gensapp-menu-announcement',
          dataTestId: 'gensapp-menu-announcement',
        },
      ];
    },
    gicButtons() {
      return [
        {
          to: '/gic/home',
          icon: HomeGICIcon,
          label: this.$t('copy.bottomNav.home'),
          dataEventTracking: 'gensapp-menu-home',
          dataTestId: 'gensapp-menu-home',
        },
        {
          to: '/gic/resources/sessions',
          icon: ResourcesGICIcon,
          label: this.$t('copy.bottomNav.resources'),
          dataEventTracking: 'gensapp-menu-resources',
          dataTestId: 'gensapp-menu-resources',
        },
        {
          to: '/gic/schedule',
          icon: ScheduleGICIcon,
          label: this.$t('copy.bottomNav.schedule'),
          dataEventTracking: 'gensapp-menu-schedule',
          dataTestId: 'gensapp-menu-schedule',
        },
      ];
    },
  },
  // do it 1 time only it loaded the page
  async mounted() {
    console.log('in Mounted: Bottom Nav');
    const conferenceType = getCurrentConferenceType(this.confYear);
    const locale = await getCurrentLocale(conferenceType);
    this.$i18n.locale = locale;
    document.querySelector('html').setAttribute('lang', locale);
  },
  methods: {
    ...mapActions('schedule', ['setLinkOpenDate', 'setScrollPosition']),
    onNavClick() {
      this.clearSchedule();
    },
    clearSchedule() {
      console.log('clear schedule here...');
      this.setLinkOpenDate('');
      this.setScrollPosition(1);
    },
  },
};
</script>
<style scoped>
/*
 * Shadow lives on the wrapper as a drop-shadow (not box-shadow on the shell):
 * drop-shadow traces the real pill shape (border-radius) and renders correctly
 * across WebKit/iOS WKWebView and Android.
 */
nav {
  filter: drop-shadow(0 5px 16px rgba(0, 0, 0, 0.13));
}

.bottom-nav-shell {
  background: #ffffff;
  display: grid;
  grid-template-columns: repeat(var(--nav-count), minmax(0, 1fr));
  /* Fixed pill width (5-icon size). fewer icons (e.g. 3) will be spread out
  instead of the pill shrinking. */
  width: min(calc(100vw - 32px), 400px);
}
</style>
